import React from 'react';

import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {LEAF_TYPE_TO_ICON} from 'data-types/benchling-type-def/leafTypeToIcon';
import {MappableColumn, MappableLayer} from 'plates/dao/ScratchPlateDAO';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

// A source column is identified by (layerName, columnName). The dropdown works
// with a single string value per option, so we encode the pair into one opaque
// token and decode it on selection. The unit separator keeps the token
// unambiguous even if a layer or column name contains other punctuation.
const TOKEN_SEPARATOR = '\u001f';

export function encodeSourceColumn(layerName: string, columnName: string): string {
  return `${layerName}${TOKEN_SEPARATOR}${columnName}`;
}

export function decodeSourceColumn(token: string): {layerName: string; columnName: string} {
  const [layerName, columnName, ...rest] = token.split(TOKEN_SEPARATOR);
  if (columnName === undefined || rest.length > 0) {
    throw new Error(`Malformed source column token; expected layerName${TOKEN_SEPARATOR}columnName.`);
  }
  return {layerName, columnName};
}

const LEAF_TYPES_BY_VALUE = new Map<string, LeafType>(Object.values(LeafType).map((lt) => [lt, lt]));

// The backend sends a leaf-type string (e.g. "Text", "Integer") that may be
// absent. Map it to a type icon, mirroring `columnToTypeIcon` for data-frame
// columns, and fall back to a neutral icon when the type is unknown.
function columnIcon(column: MappableColumn): BenchlingIconName {
  const leafType = column.leafType != null ? LEAF_TYPES_BY_VALUE.get(column.leafType) : undefined;
  return leafType != null ? LEAF_TYPE_TO_ICON[leafType] : 'text';
}

export interface ScratchPlateSourceColumnSelectorProps {
  /** Source columns, grouped by scratch-plate layer. */
  layers: Array<MappableLayer>;
  /** The currently selected source column, encoded via `encodeSourceColumn`. */
  selectedValue: string | null;
  onChange: (value: string | null) => void;
  allowSelectNull: boolean;
  disabled?: boolean;
}

/**
 * Dropdown that selects one scratch-plate source column, grouped by layer.
 *
 * This mirrors the look of the analysis tool's `ColumnDropdownSelector`
 * (a `SelectDropdown` over a `SearchableMenu` with a type-icon `Chip`), but is
 * typed on the plate-native `MappableColumn`/`MappableLayer` rather than on
 * data-frame types.
 */
export default function ScratchPlateSourceColumnSelector({
  layers,
  selectedValue,
  onChange,
  allowSelectNull,
  disabled,
}: ScratchPlateSourceColumnSelectorProps): JSX.Element {
  const columnsByValue = new Map<string, MappableColumn>();
  for (const layer of layers) {
    for (const column of layer.columns) {
      columnsByValue.set(encodeSourceColumn(layer.layerName, column.systemName), column);
    }
  }
  const selectedColumn = selectedValue != null ? columnsByValue.get(selectedValue) : undefined;

  // A header row per layer, then its columns. Headers are non-selectable so the
  // grouping reads clearly when several layers expose columns.
  const optionDatas: Array<OptionData> = [
    ...(allowSelectNull ? [{name: 'None', value: null}] : []),
    ...layers.flatMap(
      (layer): Array<OptionData> => [
        {name: layer.layerName, isHeader: true, disabled: true},
        ...layer.columns.map((column) => ({
          icon: columnIcon(column),
          name: column.displayName,
          value: encodeSourceColumn(layer.layerName, column.systemName),
        })),
      ]
    ),
  ];

  const menu = (
    <SearchableMenu
      height={MenuHeight.MEDIUM}
      width={MenuWidth.FULL}
      selectedValue={selectedValue}
      onSelect={(value): void => {
        onChange(value);
      }}
      optionDatas={optionDatas}
    />
  );

  return (
    <SelectDropdown
      menu={menu}
      disabled={disabled}
      displayName={
        selectedColumn != null ? (
          <Chip
            size={ChipSize.SMALL}
            icon={columnIcon(selectedColumn)}
            text={selectedColumn.displayName}
            isReadonly={true}
          />
        ) : (
          'None'
        )
      }
    />
  );
}
